import {
  BASE,
  DEMOTION_GRACE,
  K_STANDARD,
  MAX_SEATS,
  MARGIN_MAX,
  MARGIN_MIN,
  PLACEMENT_GAMES,
  RANKS,
  RANK_BAND,
  TAKEDOWN_LEVEL,
  TAKEDOWN_MAX,
  TAKEDOWN_MIN,
  UPSET_MAX,
  participationBonus,
} from '@edh/shared';
import { Halo } from './RankBadge';
import { Sheet } from './Sheet';

/**
 * What the halos mean, and what moves them.
 *
 * A ladder that has to be explained is a ladder somebody has to be able to look
 * up. The alternative is that the meaning lives in whoever set it up, which
 * lasts exactly as long as they are in the room.
 *
 * Rewritten as tables rather than paragraphs. The first version explained every
 * decision behind the design in prose, which is the right content for the
 * source and the wrong content for a phone: nobody opens this to read an essay,
 * they open it to find out what a rank costs or what a takedown pays. The
 * reasoning still exists — it lives in elo.ts, next to the arithmetic it
 * justifies.
 *
 * Every number here is read from the constants rather than typed out. The old
 * copy said playing paid "a couple of points, nothing at all by Ascended",
 * which had been true two curves ago and quietly became a lie the moment the
 * numbers moved. Prose about arithmetic goes stale; this cannot.
 */

export function RankGuideSheet({ onClose }: { onClose: () => void }) {
  /**
   * How many games a rank costs on the bonus alone, at that rating.
   *
   * One answer again. The bonus spent a while scaled by the table you sat at,
   * which meant this had to be quoted for a particular size; it is flat per
   * game now, and the size of the room is carried by the result instead.
   */
  const gamesPerRank = (elo: number) => Math.round(RANK_BAND / participationBonus(elo));

  /**
   * What winning an even game is worth, read off the constants.
   *
   * `K / 2` at a duel, times the root of the opponents faced — the pairwise
   * scale — then the margin multiplier at either end of its band. Quoted from
   * the duel at the bottom and a full pod at the top, so the number covers
   * every table the guild sits at. Computed rather than written down, because
   * it was written down once and went stale the moment K moved.
   */
  const evenAt = (players: number) => (K_STANDARD / 2) * Math.sqrt(players - 1);
  const swing = {
    low: Math.round(evenAt(2) * MARGIN_MIN),
    high: Math.round(evenAt(MAX_SEATS) * MARGIN_MAX),
  };

  return (
    <Sheet title="Ranks" onClose={onClose}>
      <p className="hint" style={{ marginTop: -4 }}>
        Every {RANK_BAND} points is a rank. The halo round somebody’s picture is theirs,
        everywhere they appear.
      </p>

      <div className="rank-guide">
        {[...RANKS].reverse().map((rank) => {
          const start = rank.floor === BASE;
          return (
            <div className={`rank-guide-row${start ? ' start' : ''}`} key={rank.name}>
              <span className="rank-guide-halo">
                <Halo style={rank.halo} color={rank.color} glow={rank.glow} />
              </span>
              <span className="rank-guide-name" style={{ color: rank.color }}>
                {rank.name}
              </span>
              {start && <span className="rank-guide-start">you start here</span>}
              <span className="rank-guide-band num">
                {Number.isFinite(rank.floor) ? `${rank.floor}+` : `under ${RANKS[1]!.floor}`}
              </span>
            </div>
          );
        })}
      </div>

      {/*
        The part people actually open this for.

        The first row is the only one that can go down, and the only one that
        decides who is ahead of whom. The three below it are bounties: they add
        or they do not happen, so they move the whole guild up the ladder
        without moving anybody past anybody.
      */}
      <div className="section-head">
        <h2>Earning points</h2>
      </div>
      <div className="guide-table">
        {/*
          The attendance bonus is not named here, and that is deliberate.

          It was a row of its own, then a clause in this one, and both made it
          sound like a separate thing you could go and collect. Nobody
          experiences it that way: you play a game, your rating moves, and it
          moves less the higher you are. That is the whole of what a player
          needs, and it is true. The mechanism behind it — that part of the
          movement is paid rather than won, and is subtracted back out before
          anything predicts anything — is real and belongs in elo.ts, not on a
          phone screen at a table.
        */}
        <div className="guide-row">
          <span className="guide-what">
            Playing, winning, finishing high
            <span className="guide-note">
              the bigger the table the further it moves you — and turning up is worth
              less the higher you climb, about {gamesPerRank(BASE)} games a rank at{' '}
              {RANKS.find((r) => r.floor === BASE)?.name} and {gamesPerRank(RANKS[7]!.floor)}{' '}
              at {RANKS[7]!.name}
            </span>
          </span>
          <span className="guide-amount num">±{swing.low}–{swing.high}</span>
        </div>
        <div className="guide-row">
          <span className="guide-what">
            Knocking somebody out
            <span className="guide-note">
              3+ players; {TAKEDOWN_LEVEL} for a seat level with yours, more the further
              above you it was and less the further below
            </span>
          </span>
          <span className="guide-amount num">
            +{TAKEDOWN_MIN}–{TAKEDOWN_MAX}
          </span>
        </div>
        <div className="guide-row">
          <span className="guide-what">
            Winning against the odds
            <span className="guide-note">
              the biggest prize going, and the rarest — nothing unless the table
              had you below an even share
            </span>
          </span>
          <span className="guide-amount num">+0–{UPSET_MAX}</span>
        </div>
      </div>
      <p className="hint" style={{ marginTop: 8 }}>
        Only the first is taken off anybody. The rest are added — they move the whole guild up
        the ladder, and never past each other.
      </p>

      <div className="section-head">
        <h2>The small print</h2>
      </div>
      <div className="guide-table">
        {/* The old wording — "no halo for your first 5 games" — read as a
            penalty, as though a rank were being withheld. It is the opposite:
            there is no rank yet to show, because five games is what it takes
            to have one at all. */}
        {/* Both of these are the same idea from two sides: a number nobody has
            played for yet does not count for anything yet. */}
        <div className="guide-row">
          <span className="guide-what">A bigger table pays more</span>
          <span className="guide-note">
            a three-player game is worth half again as much as a duel, a four-player game twice
          </span>
        </div>
        <div className="guide-row">
          <span className="guide-what">A brand new deck is treated as an unknown</span>
          <span className="guide-note">
            its bracket counts against you in full from its {PLACEMENT_GAMES}th game
          </span>
        </div>
        <div className="guide-row">
          <span className="guide-what">Your rank appears after {PLACEMENT_GAMES} games</span>
          <span className="guide-note">until then it is still finding your level</span>
        </div>
        <div className="guide-row">
          <span className="guide-what">Promoted the moment you cross</span>
          <span className="guide-note">
            losing it takes two: the fall, then a game that does not win the ground back
          </span>
        </div>
        <div className="guide-row">
          <span className="guide-what">
            In between you are <span className="rank-held">in grace</span>
          </span>
          <span className="guide-note">
            the halo breaks, and the next game you go backwards in takes the rank —
            a game you gain in keeps it, even below the line
          </span>
        </div>
        <div className="guide-row">
          <span className="guide-what">Unless the fall is a bad one</span>
          <span className="guide-note">
            more than {DEMOTION_GRACE} below in one result and it goes there and then
          </span>
        </div>
      </div>
      <p className="hint" style={{ marginTop: 8 }}>
        It says something real over a season and very little over an evening. The accuracy panel
        under the ladder is the honest check on whether it is measuring anything at all.
      </p>
    </Sheet>
  );
}
